import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiCall } from '../config/api';
import './ClassDetails.css';

const ClassDetails = ({ user }) => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Redirect to auth if not logged in or not a teacher
    if (!user || user.role !== 'teacher') {
      navigate('/auth');
      return;
    }

    loadClassDetails();
  }, [user, navigate, classId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadClassDetails = async () => {
    try {
      // Check if classId is valid before making API call
      if (!classId || classId === 'undefined') {
        console.error('Invalid class ID:', classId);
        alert('Invalid class ID. Redirecting to dashboard.');
        navigate('/teacher-dashboard');
        return;
      }

      const response = await apiCall(`/classes/${classId}`);
      if (response.ok) {
        const data = await response.json();
        setClassData(data);
      } else {
        console.error('Failed to load class details');
        alert('Failed to load class details. Redirecting to dashboard.');
        navigate('/teacher-dashboard');
      }
    } catch (error) {
      console.error('Error loading class details:', error);
      alert('Error loading class details. Redirecting to dashboard.');
      navigate('/teacher-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStudentStats = (student) => {
    const gamesPlayed = student.studentProfile?.totalGamesPlayed || 0;
    const totalQuestions = student.studentProfile?.totalQuestionsAnswered || 0;
    const correctAnswers = student.studentProfile?.totalCorrectAnswers || 0;
    
    // Win rate is based on games won (not quiz accuracy)
    // For now, we'll use quiz accuracy as a proxy since we don't track wins separately
    const winRate = gamesPlayed > 0 && totalQuestions > 0 ? 
      ((correctAnswers / totalQuestions) * 100).toFixed(1) : '0.0';
    
    const quizAccuracy = totalQuestions > 0 ? 
      ((correctAnswers / totalQuestions) * 100).toFixed(1) : '0.0';
    
    return {
      gamesPlayed,
      winRate,
      quizAccuracy,
      lastPlayed: student.updatedAt ? new Date(student.updatedAt).toLocaleDateString() : 'Never'
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Class code copied to clipboard!');
  };

  const removeStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to remove this student from the class?')) {
      return;
    }

    try {
      const response = await apiCall(`/classes/${classId}/students/${studentId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Reload class data
        loadClassDetails();
        alert('Student removed successfully.');
      } else {
        console.error('Failed to remove student');
        alert('Failed to remove student. Please try again.');
      }
    } catch (error) {
      console.error('Error removing student:', error);
      alert('Error removing student. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="class-details-loading">
        <div className="loading-spinner"></div>
        <p>Loading class details...</p>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="class-details-error">
        <h2>Class not found</h2>
        <button onClick={() => navigate('/teacher-dashboard')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  console.log('Full classData received:', classData);
  const students = classData.class?.students || classData.students || [];
  console.log('Students extracted:', students);

  return (
    <div className="class-details">
      <div className="class-details-header">
        <button className="back-button" onClick={() => navigate('/teacher-dashboard')}>
          ← Back to Dashboard
        </button>
        <div className="class-info">
          <h1>📚 {classData.class?.className || classData.className}</h1>
          <div className="class-meta">
            <span className="class-code-display">
              Code: {classData.class?.classCode || classData.classCode}
              <button 
                className="copy-btn-inline" 
                onClick={() => copyToClipboard(classData.class?.classCode || classData.classCode)}
              >
                📋
              </button>
            </span>
            <span className="class-created">Created: {formatDate(classData.class?.createdAt || classData.createdAt)}</span>
          </div>
        </div>
      </div>

      <div className="class-details-content">
        {/* Class Statistics Overview */}
        {students.length > 0 && (
          <div className="class-stats-card">
            <h2>📊 Class Overview</h2>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-number">{students.length}</span>
                <span className="stat-label">Total Students</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {students.reduce((total, student) => {
                    const stats = getStudentStats(student);
                    return total + stats.gamesPlayed;
                  }, 0)}
                </span>
                <span className="stat-label">Total Games Played</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {students.length > 0 ? Math.round(
                    students.reduce((total, student) => {
                      const stats = getStudentStats(student);
                      return total + parseFloat(stats.winRate);
                    }, 0) / students.length
                  ) : 0}%
                </span>
                <span className="stat-label">Average Win Rate</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {students.length > 0 ? Math.round(
                    students.reduce((total, student) => {
                      const stats = getStudentStats(student);
                      return total + parseFloat(stats.quizAccuracy);
                    }, 0) / students.length
                  ) : 0}%
                </span>
                <span className="stat-label">Average Quiz Accuracy</span>
              </div>
            </div>
          </div>
        )}

        {/* Students Section */}
        <div className="students-card">
          <h2>👥 Students ({students.length})</h2>
          {students.length > 0 ? (
            <div className="students-table">
              <div className="table-header">
                <span>Student</span>
                <span>Games Played</span>
                <span>Win Rate</span>
                <span>Quiz Accuracy</span>
                <span>Last Played</span>
                <span>Actions</span>
              </div>
              {students.map((student, index) => {
                const stats = getStudentStats(student);
                return (
                  <div key={student._id || index} className="table-row">
                    <span className="student-name">
                      {student.firstName} {student.lastName}
                    </span>
                    <span className="games-played">{stats.gamesPlayed}</span>
                    <span className="win-rate">{stats.winRate}%</span>
                    <span className="quiz-accuracy">{stats.quizAccuracy}%</span>
                    <span className="last-played">{stats.lastPlayed}</span>
                    <span className="actions">
                      <button 
                        className="remove-btn"
                        onClick={() => removeStudent(student._id)}
                        title="Remove student from class"
                      >
                        🗑️
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-students">
              <p>No students have joined this class yet.</p>
              <p>Share the class code <strong>{classData.classCode}</strong> with your students!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassDetails;
